import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import ToastsContainer from 'CommonComponents/toast/ToastContainer';

export const ToastContext = createContext();

export const useToastContext = () => {
    const context = useContext(ToastContext);

    if (!context) {
        throw new Error('useToast must be used within a ToastContext');
    }

    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((type, message, duration) => {
        const id = Math.floor(Math.random() * 10000000);
        setToasts((oldToasts) =>
            oldToasts.some((toast) => toast.type === type && toast.message === message) ? oldToasts : [...oldToasts, { id, message, type, duration }],
        );
    }, []);

    const remove = useCallback((id) => {
        setToasts((oldToasts) => oldToasts.filter((toast) => toast.id !== id));
    }, []);

    // Consumers list `toast` in hook dependencies, so a new object per render would re-run
    // their effects every time a toast is added or closed.
    const value = useMemo(
        () => ({
            success: (message, duration) => addToast('success', message, duration),
            warning: (message, duration) => addToast('warning', message, duration),
            info: (message, duration) => addToast('info', message, duration),
            error: (message, duration) => addToast('error', message, duration),
            remove,
        }),
        [addToast, remove],
    );

    return (
        <ToastContext.Provider value={value}>
            {createPortal(<ToastsContainer toasts={toasts} />, document.body)}
            {children}
        </ToastContext.Provider>
    );
};

export default ToastContext;
